/**
 * The acts an officer performs on a case, and the timeline they produce.
 *
 * Everything here posts a **command** — `record-service`, `record-decision`,
 * an extension on a running clock, a skip with its reason. Not a form that
 * PATCHes a field: what happened and what a field should read are different
 * statements, and the difference is the whole audit trail.
 *
 * Three rules this file follows and does not get to relax:
 *
 * **The server decides what is possible.** Nothing here computes whether an act
 * is available. Where the server refuses, its envelope is rendered — the rule,
 * the citation, the missing list — rather than a status code or a sentence this
 * screen made up.
 *
 * **A period that starts, says so.** Recording service is the act that starts a
 * clock, and the receipt names which one, so the officer sees the consequence of
 * what they just did rather than having to go and look.
 *
 * **A date is the date of the ACT.** Not today, not the date of recording.
 * Backdating is normal and correct here; the field defaults to today because
 * that is the common case, and it is an ordinary editable date — **except where
 * the date is read off a paper and starts or closes a period** (the service
 * date, the day his reply arrived), which starts blank, as on the case page's
 * card: a started period's start is never moved afterwards, and these forms are
 * for acts done on paper before the case was in the app, where today is the
 * least likely date of all (compliance review, 26 Sep 2026). Nor is an answer
 * picked for the officer: a decision's outcome starts empty.
 *
 * ---
 *
 * **Caller since 26 Sep 2026:** the case page's *Record something else* mounts
 * `RecordService`, `RecordDecisions`, `RecordReply`, `RecordApproval` and
 * `RecordFact`, and its case file shows `Timeline` (docs/19 step 5). What
 * follows is the ruling that hid them.
 *
 * **Nothing in this file currently has a caller. That is deliberate, and it is
 * not dead code.** Ruled 29 Aug 2026 under the scope ruling — this is a
 * drafting app, and these are case management (walkthrough log entries 16-20,
 * 22, and entry 15's answer):
 *
 * - `RecordService`, `RecordDecisions`, `RecordReply`, `Timeline` — the command
 *   cards and the record of having used them, hidden from the case page. Their
 *   re-placement is where the acts belong: service on the document row, a
 *   decision at its decision point, the reply on the period it closes.
 * - `GrantExtension` — hidden with the Periods card, until the clock lengths
 *   are verified against the rule text.
 * - `SkipOrWithdraw` — "just Generate for the one to be generated, and View for
 *   the one already generated". No surface records a skip now.
 *
 * The commands, their guards and their audit actions are all untouched on the
 * server. Kept rather than deleted because each is the built, reviewed surface
 * for an act this product will need again, and rebuilding one from the git
 * history is strictly worse than reading it here. **Delete only on a ruling
 * that the act itself is gone** — not because a linter called it unused.
 */

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../api/client'
import type { CaseDetail, ClockView, DocumentRow } from '../api/client'
import { ErrorEnvelope } from './bits'
import { readDate } from '../lib/period'

/**
 * The organisation's date, off the response — never the browser's.
 *
 * `today` is resolved once, at the router/service boundary, in `ORG_TIMEZONE`.
 * The browser is a different clock in a different zone: on a workstation left
 * on UTC — common on standardised enterprise images — 01:00 IST on 12/08 reads
 * as 11/08. A pre-filled `served_on` accepted at face value then starts the
 * Rule 14(4) period a day early, and every "overdue by N" on that case, on
 * every surface, is off by one for the life of the proceeding.
 *
 * `as_of` is the same date the clocks on this very response were evaluated
 * against, so the form and the pills cannot disagree.
 */
function asOf(data: CaseDetail): string {
  return data.as_of
}

function useRefresh(caseId: string) {
  const client = useQueryClient()
  return () => {
    void client.invalidateQueries({ queryKey: ['case', caseId] })
    void client.invalidateQueries({ queryKey: ['transitions', caseId] })
    void client.invalidateQueries({ queryKey: ['audit', caseId] })
    void client.invalidateQueries({ queryKey: ['notifications'] })
  }
}

/* ------------------------------------------------------------- service --- */

/**
 * Recording service or delivery — the पावती.
 *
 * **This is where three clocks start**, and the date is why: Rule 14(4) runs
 * from *receipt*, and Rule 25 from *delivery* of the penalty order, never from
 * the date it was made.
 *
 * The card also took the पावती's own reference number. Nothing ever read it —
 * it went into the event payload and stopped there, reaching no template, no
 * check and no clock — so it was a field to fill on the way to the one that
 * matters. The पावती is uploaded as a paper; the scan is the record of it.
 */
export function RecordService({ data }: { data: CaseDetail }) {
  const refresh = useRefresh(data.id)
  // **Every row the rail returns.** This used to drop `skipped` and
  // `out-of-scope` rows, which is a rule the client invented: the server
  // refuses only a future date on `record-service`. A screen that decides for
  // itself is a second state machine, and this one failed in the direction
  // where the document whose पावती is in the officer's hand is simply not in
  // the list — no rule beside it, no reason, nothing to click.
  const servable = data.rail ?? []
  // Neither is chosen for the officer: the document is the one whose पावती is
  // in hand, and the date is the one written on it — it starts a period that
  // is never moved afterwards (compliance review, 26 Sep 2026).
  const [documentType, setDocumentType] = useState('')
  const [servedOn, setServedOn] = useState('')
  // **A success with no clock said nothing at all.** The only sign the command
  // had landed was the started-period chip, and most of the twenty start no
  // period — so recording service on D01 looked exactly like a dead button.
  // Clicked three times, it wrote three identical served events.
  const [done, setDone] = useState<{ documentType: string; servedOn: string } | null>(null)

  const record = useMutation({
    mutationFn: () =>
      api.recordService(data.id, {
        preflight: false,
        document_type: documentType,
        occurrence: 1,
        served_on: servedOn,
      }),
    onSuccess: () => {
      setDone({ documentType, servedOn })
      refresh()
    },
  })

  const started = record.data?.clocks_started ?? []

  return (
    <div className="card">
      <header>
        <h2>Record service</h2>
        <span className="small muted">The date of delivery, not the date it was recorded</span>
      </header>
      <div className="body">
        <div className="row form">
          <label>
            Document
            <select
              aria-label="Document served"
              value={documentType}
              onChange={(event) => {
                setDone(null)
                setDocumentType(event.target.value)
              }}
            >
              <option value="">— the document served —</option>
              {servable.map((row) => (
                <option key={row.document_type} value={row.document_type}>
                  {row.document_type} — {row.title}
                </option>
              ))}
            </select>
          </label>
          <label>
            Served on
            <input
              type="date"
              aria-label="Served on"
              value={servedOn}
              onChange={(event) => setServedOn(event.target.value)}
            />
          </label>
        </div>
        <div className="row">
          <button
            type="button"
            className="primary"
            data-testid="record-service"
            disabled={record.isPending || !documentType || !servedOn}
            onClick={() => record.mutate()}
          >
            {record.isPending ? 'Recording…' : 'Record service'}
          </button>
          {started.length ? (
            <span className="chip good" data-testid="clock-started">
              {started.length === 1
                ? 'A period started from that date'
                : `${started.length} periods started from that date`}
            </span>
          ) : done ? (
            <span className="chip good" data-testid="service-recorded">
              {done.documentType} served on {readDate(done.servedOn)}
            </span>
          ) : null}
        </div>
        <ErrorEnvelope error={record.error} />
      </div>
    </div>
  )
}

/* ------------------------------------------------------------ approval --- */

/**
 * The approving authority endorsed a draft.
 *
 * **A distinct act from issuing and from serving**, and two guards read its
 * date: a ज्ञापन is approved on the noting sheet before it is signed out, and
 * `CR-14-05C` will not let a case into the formal inquiry without a recorded
 * order on the noting sheet constituting the Court of Enquiry.
 *
 * The command has existed since the transitions were built and **no screen
 * ever called it** — the client method sat in `api/client.ts` with no caller.
 * So a backfilled case walked its missing acts one at a time, satisfied the
 * service guard and the response guard, and then stopped for ever at a guard
 * whose fact nothing in the app could write. That is the officers' own
 * complaint at its root: *the case stage is not progressing even though the
 * case has gone ahead.* Found in rehearsal, 18 Sep 2026.
 */
export function RecordApproval({ data }: { data: CaseDetail }) {
  const refresh = useRefresh(data.id)
  const rows = data.rail ?? []
  const [documentType, setDocumentType] = useState(rows[0]?.document_type ?? '')
  const [approvedOn, setApprovedOn] = useState(asOf(data))
  const [approvedBy, setApprovedBy] = useState('')
  const [done, setDone] = useState<string | null>(null)

  const record = useMutation({
    mutationFn: () =>
      api.recordApproval(data.id, {
        preflight: false,
        document_type: documentType,
        occurrence: 1,
        approved_on: approvedOn,
        approved_by: approvedBy.trim() || null,
      }),
    onSuccess: () => {
      setDone(documentType)
      refresh()
    },
  })

  return (
    <div className="card">
      <header>
        <h2>Record an approval</h2>
        <span className="small muted">The endorsement on the noting, before it went out</span>
      </header>
      <div className="body">
        <div className="row form">
          <label>
            Document
            <select
              aria-label="Document approved"
              value={documentType}
              onChange={(event) => {
                setDone(null)
                setDocumentType(event.target.value)
              }}
            >
              {rows.map((row) => (
                <option key={row.document_type} value={row.document_type}>
                  {row.document_type} — {row.title}
                </option>
              ))}
            </select>
          </label>
          <label>
            Approved on
            <input
              type="date"
              aria-label="Approved on"
              value={approvedOn}
              onChange={(event) => setApprovedOn(event.target.value)}
            />
          </label>
          <label>
            Approved by
            <input
              aria-label="Approved by"
              placeholder="the post that endorsed it"
              value={approvedBy}
              onChange={(event) => setApprovedBy(event.target.value)}
            />
          </label>
        </div>
        <div className="row">
          <button
            type="button"
            className="primary"
            data-testid="record-approval"
            disabled={record.isPending || !documentType}
            onClick={() => record.mutate()}
          >
            {record.isPending ? 'Recording…' : 'Record the approval'}
          </button>
          {done ? (
            <span className="chip good" data-testid="approval-recorded">
              {done} approved on {readDate(approvedOn)}
            </span>
          ) : null}
        </div>
        <ErrorEnvelope error={record.error} />
      </div>
    </div>
  )
}

/* --------------------------------------------------------- appointment --- */

/**
 * Who was appointed to a role. **People, not just documents.**
 *
 * Every document that names the Inquiry Officer — D02, D03, D09, D10, D11,
 * D13, D14 — carries `io_name` and `io_designation`, and both fill from a
 * recorded appointment. Record it once and seven documents stop asking.
 * Without it each one renders an amber blank and the officer types the same
 * name seven times, which is seven chances to type it differently.
 *
 * It is also what `CR-14-02` compares: the Inquiry Officer must not be the
 * Disciplinary Authority, the Presenting Officer or a listed witness, and that
 * is a comparison of person identities. Appointment used to be recorded only
 * as a document (D09), so the comparison read nothing and the guard was a
 * permanent closed gate.
 *
 * `record-appointment` has existed since the transitions were built and, like
 * `record-approval`, **no screen ever called it** — found in rehearsal,
 * 18 Sep 2026.
 */
export function RecordAppointment({ data }: { data: CaseDetail }) {
  const refresh = useRefresh(data.id)
  const [role, setRole] = useState<'IO' | 'PO' | 'defence_assistant'>('IO')
  const [name, setName] = useState('')
  const [designation, setDesignation] = useState('')
  const [appointedOn, setAppointedOn] = useState(asOf(data))
  const [receivedOn, setReceivedOn] = useState('')
  const [done, setDone] = useState<string | null>(null)

  const record = useMutation({
    mutationFn: () =>
      api.recordAppointment(data.id, {
        preflight: false,
        role,
        appointed_on: appointedOn,
        person_name: name.trim() || null,
        designation: designation.trim() || null,
        received_on: role === 'IO' && receivedOn ? receivedOn : null,
      }),
    onSuccess: () => {
      setDone(name.trim())
      refresh()
    },
  })

  return (
    <div className="card">
      <header>
        <h2>Record an appointment</h2>
        <span className="small muted">
          Recorded once — every document that names them fills
        </span>
      </header>
      <div className="body">
        <div className="row form">
          <label>
            Role
            <select
              aria-label="Role appointed"
              value={role}
              onChange={(event) => {
                setDone(null)
                setRole(event.target.value as 'IO' | 'PO' | 'defence_assistant')
              }}
            >
              <option value="IO">Inquiry Officer</option>
              <option value="PO">Presenting Officer</option>
              <option value="defence_assistant">Defence Assistant</option>
            </select>
          </label>
          <label>
            Name
            <input
              aria-label="Name of the appointee"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </label>
          <label>
            Designation
            <input
              aria-label="Designation of the appointee"
              value={designation}
              onChange={(event) => setDesignation(event.target.value)}
            />
          </label>
        </div>
        <div className="row form">
          <label>
            Appointed on
            <input
              type="date"
              aria-label="Appointed on"
              value={appointedOn}
              onChange={(event) => setAppointedOn(event.target.value)}
            />
          </label>
          {/* **Rule 14(24)'s six months run from receipt, not from issue.** */}
          {role === 'IO' ? (
            <label>
              Order received on
              <input
                type="date"
                aria-label="Order received on"
                value={receivedOn}
                onChange={(event) => setReceivedOn(event.target.value)}
              />
            </label>
          ) : null}
        </div>
        <div className="row">
          <button
            type="button"
            className="primary"
            data-testid="record-appointment"
            disabled={record.isPending || !name.trim()}
            onClick={() => record.mutate()}
          >
            {record.isPending ? 'Recording…' : 'Record the appointment'}
          </button>
          {done ? (
            <span className="chip good" data-testid="appointment-recorded">
              {done} recorded
            </span>
          ) : null}
        </div>
        <ErrorEnvelope error={record.error} />
      </div>
    </div>
  )
}

/* ---------------------------------------------------------------- fact --- */

/** A path as an officer reads it. The vocabulary is the engine's; the words
 *  are not, and a dotted path in a dropdown is the software talking to itself. */
const FACT_WORDS: Record<string, string> = {
  'case.organisation.s14_3_notified':
    'AWEIL is notified under s. 14(3) of the Administrative Tribunals Act',
  'case.disagreement_exists': 'The Disciplinary Authority disagrees with the findings',
  'case.appeal.filed_on': 'An appeal was filed on',
  'case.appeal_status': 'The appeal was decided',
  'case.revision.filed_on': 'A revision petition was filed on',
  'case.rejoinder.filed_on': 'A rejoinder was filed on',
  'case.litigation.oa_filed_on': 'An Original Application was filed on',
  'case.litigation.sur_rejoinder_leave_on': 'Leave for a sur-rejoinder was granted on',
  'case.litigation.writ_filed_on': 'A writ petition was filed on',
  'case.litigation.department_writ_decided_on': "The department's writ was decided on",
  'case.sur_rejoinder.leave_order_date': 'The order granting leave is dated',
  'case.suspension_status': 'The Charged Officer is under suspension',
  'case.proposed_penalty_kind': 'The penalty proposed is',
  'case.preliminary_enquiry.conclusion': 'The preliminary enquiry concluded',
  'case.ex_parte.precautions_recorded': 'The ex-parte precautions are recorded',
  'case.ex_parte.warning_notices_served': 'The ex-parte warning notices were served',
  'case.reply.read_down_note': 'The reply is read down, because',
  'case.reply_reminders': 'Reminders sent to the Charged Officer',
  'case.chargesheet.witness_person_ids': 'The listed witnesses are',
  'case.disciplinary_authority.person_id': 'The Disciplinary Authority is',
}

/** Which facts read as yes/no rather than as a value. */
const YES_NO = new Set([
  'case.organisation.s14_3_notified',
  'case.disagreement_exists',
  'case.suspension_status',
  'case.ex_parte.precautions_recorded',
  'case.ex_parte.warning_notices_served',
])

/**
 * A fact a guard reads, and nothing else writes.
 *
 * **A guard that turns on a fact nothing can write is a gate nobody can open.**
 * Three were found in one rehearsal, 18 Sep 2026: the approval on the noting
 * sheet constituting the Court of Enquiry, the Inquiry Officer's appointment,
 * and AWEIL's notification status under s. 14(3) of the Administrative
 * Tribunals Act. The first two are acts and got their own commands long ago;
 * this is the rest, and `record-fact` has always accepted them.
 *
 * The list is the pinned version's own, off the server (stop list 6):
 * `recordable_paths` collects every path a rule references and drops the ones
 * another command owns, so a route or a reply cannot be written here behind
 * the command that means it. The words in front of the paths are this screen's,
 * because a dotted path in a dropdown is the engine talking to itself; a path
 * with no wording yet shows as itself rather than not at all.
 */
export function RecordFact({ data }: { data: CaseDetail }) {
  const refresh = useRefresh(data.id)
  const paths = data.recordable_facts ?? []
  const [path, setPath] = useState(paths[0] ?? '')
  const [value, setValue] = useState('')
  const [recordedOn, setRecordedOn] = useState(asOf(data))
  const [done, setDone] = useState<string | null>(null)

  const yesNo = YES_NO.has(path)
  const record = useMutation({
    mutationFn: () =>
      api.recordFact(data.id, {
        preflight: false,
        path,
        value: yesNo ? value === 'yes' : value.trim() || null,
        recorded_on: recordedOn,
        note: null,
      }),
    onSuccess: () => {
      setDone(FACT_WORDS[path] ?? path)
      refresh()
    },
  })

  if (paths.length === 0) return null
  return (
    <div className="card">
      <header>
        <h2>Record a fact</h2>
        <span className="small muted">Facts this case&apos;s rules read</span>
      </header>
      <div className="body">
        <div className="row form">
          <label>
            The fact
            <select
              aria-label="The fact"
              value={path}
              onChange={(event) => {
                setDone(null)
                setValue('')
                setPath(event.target.value)
              }}
            >
              {paths.map((option) => (
                <option key={option} value={option}>
                  {FACT_WORDS[option] ?? option}
                </option>
              ))}
            </select>
          </label>
          <label>
            {yesNo ? 'Is it so?' : 'Value'}
            {yesNo ? (
              <select
                aria-label="The value"
                value={value}
                onChange={(event) => setValue(event.target.value)}
              >
                <option value="">— choose —</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            ) : (
              <input
                aria-label="The value"
                value={value}
                onChange={(event) => setValue(event.target.value)}
              />
            )}
          </label>
          <label>
            Recorded on
            <input
              type="date"
              aria-label="Recorded on"
              value={recordedOn}
              onChange={(event) => setRecordedOn(event.target.value)}
            />
          </label>
        </div>
        <div className="row">
          <button
            type="button"
            className="primary"
            data-testid="record-fact"
            disabled={record.isPending || !path || (yesNo && !value)}
            onClick={() => record.mutate()}
          >
            {record.isPending ? 'Recording…' : 'Record the fact'}
          </button>
          {done ? (
            <span className="chip good" data-testid="fact-recorded">
              Recorded · {done}
            </span>
          ) : null}
        </div>
        <ErrorEnvelope error={record.error} />
      </div>
    </div>
  )
}

/* ----------------------------------------------------------- decisions --- */

/**
 * The forks. **Recorded, never inferred** — a case with no recorded decision has
 * taken no branch, and every downstream row says so rather than guessing from
 * what documents happen to exist.
 */
export function RecordDecisions({ data }: { data: CaseDetail }) {
  const refresh = useRefresh(data.id)
  return (
    <div className="card">
      <header>
        <h2>Decisions</h2>
        <span className="small muted">Recorded with an actor and a date. Never inferred.</span>
      </header>
      <div className="body">
        {(data.decision_points ?? []).map((point) => (
          <DecisionRow
            key={point.key}
            caseId={data.id}
            point={point}
            asOf={asOf(data)}
            onDone={refresh}
          />
        ))}
      </div>
    </div>
  )
}

function DecisionRow({
  caseId,
  point,
  asOf,
  onDone,
}: {
  caseId: string
  point: NonNullable<CaseDetail['decision_points']>[number]
  asOf: string
  onDone: () => void
}) {
  // No answer is picked for the officer: "Record again" to correct a date must
  // not quietly record the first outcome in the list (compliance review,
  // 26 Sep 2026 — a route corrected here would have become Rule 14).
  const [outcome, setOutcome] = useState('')
  const [decidedOn, setDecidedOn] = useState(asOf)
  const record = useMutation({
    mutationFn: () =>
      api.recordDecision(caseId, {
        preflight: false,
        decision: point.key,
        outcome,
        decided_on: decidedOn,
        note: null,
      }),
    onSuccess: onDone,
  })

  return (
    <div className="decision-block" data-testid={`decision-${point.key}`}>
      <div className="row" style={{ alignItems: 'baseline' }}>
        <strong>{point.label}</strong>
        {point.recorded ? (
          <span className="chip good">Recorded — {point.recorded_label}</span>
        ) : (
          <span className="chip neutral">Awaiting decision</span>
        )}
      </div>
      <div className="row form">
        <label>
          Outcome
          <select
            aria-label={`${point.label} outcome`}
            value={outcome}
            onChange={(event) => setOutcome(event.target.value)}
          >
            <option value="">— the answer the authority gave —</option>
            {(point.outcomes ?? []).map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Decided on
          <input
            type="date"
            aria-label={`${point.label} date`}
            value={decidedOn}
            onChange={(event) => setDecidedOn(event.target.value)}
          />
        </label>
        <button
          type="button"
          disabled={record.isPending || !outcome}
          onClick={() => record.mutate()}
        >
          {point.recorded ? 'Record again' : 'Record'}
        </button>
      </div>
      <ErrorEnvelope error={record.error} />
    </div>
  )
}

/* ----------------------------------------------------------- the reply --- */

/** The Charged Officer's written response. It closes a statutory period. */
export function RecordReply({ data }: { data: CaseDetail }) {
  const refresh = useRefresh(data.id)
  const [to, setTo] = useState<'chargesheet' | 'inquiry-report'>('chargesheet')
  // Blank: the day it reached the office closes a statutory period, and it is
  // read off his paper, not assumed to be today (compliance review, 26 Sep).
  const [receivedOn, setReceivedOn] = useState('')
  const [mode, setMode] = useState('')

  const record = useMutation({
    mutationFn: () =>
      api.recordReply(data.id, {
        preflight: false,
        to,
        received_on: receivedOn,
        mode: mode.trim() || null,
        reference: null,
        read_down_note: null,
        note: null,
      }),
    onSuccess: () => {
      setMode('')
      refresh()
    },
  })

  const detail = record.data?.detail as Record<string, unknown> | undefined

  return (
    <div className="card">
      <header>
        <h2>The Charged Officer answered</h2>
        <span className="small muted">This closes the period it answers</span>
      </header>
      <div className="body">
        <div className="row form">
          <label>
            Answering
            <select
              aria-label="What the response answers"
              value={to}
              onChange={(event) => setTo(event.target.value as typeof to)}
            >
              <option value="chargesheet">
                The chargesheet — written statement of defence, Rule 14(4)
              </option>
              <option value="inquiry-report">
                The inquiry report — representation, Rule 15(2)
              </option>
            </select>
          </label>
          <label>
            Received on
            <input
              type="date"
              aria-label="Received on"
              value={receivedOn}
              onChange={(event) => setReceivedOn(event.target.value)}
            />
          </label>
          <label>
            Mode
            <input
              aria-label="Mode of receipt"
              placeholder="By hand, by post…"
              value={mode}
              onChange={(event) => setMode(event.target.value)}
            />
          </label>
        </div>
        <div className="row">
          <button
            type="button"
            data-testid="record-reply"
            disabled={record.isPending || !receivedOn}
            onClick={() => record.mutate()}
          >
            {record.isPending ? 'Recording…' : 'Record the response'}
          </button>
          {detail?.['satisfied'] ? (
            <span className="chip good">Period closed</span>
          ) : detail?.['detail'] ? (
            /* Recorded, and honest about what it could not do. */
            <span className="small warn-text">{String(detail['detail'])}</span>
          ) : null}
        </div>
        <ErrorEnvelope error={record.error} />
      </div>
    </div>
  )
}

/* ---------------------------------------------------------- extensions --- */

/**
 * More time on a running period.
 *
 * **The reason is mandatory everywhere**, because an extension of a statutory
 * period is the Disciplinary Authority's act to defend. The remaining headroom
 * is shown against the ceiling, and a grant that would breach it is refused by
 * the server with the proviso cited — this screen never decides that for itself.
 *
 * **A period that has not started is greyed, with its rule** — the act it is
 * waiting on, in the server's own words. It used to be hidden, and hiding it was
 * the only thing preventing the grant: the domain refused a satisfied period, a
 * non-extendable rule and an over-cap grant, and had no refusal at all for an
 * unstarted one. The clocks module says a greyed-out button is a courtesy and
 * the rule lives in the domain; both halves are now true.
 *
 * The grant carries an **idempotency key**, because this is the one command
 * where a replay is not a no-op: the ledger appends, so a lost response and a
 * second click is a second grant on the same day for the same reason, and half
 * the Rule 14(4) cap spent on it.
 */
export function GrantExtension({
  caseId,
  clock,
  asOf,
}: {
  caseId: string
  clock: ClockView
  asOf: string
}) {
  const refresh = useRefresh(caseId)
  const [days, setDays] = useState(15)
  const [reason, setReason] = useState('')
  const [grantedOn, setGrantedOn] = useState(asOf)
  const [open, setOpen] = useState(false)
  const [attempt] = useState(() => crypto.randomUUID())

  const grant = useMutation({
    mutationFn: () =>
      api.grantExtension(
        caseId,
        clock.key,
        {
          preflight: false,
          days,
          reason: reason.trim(),
          granted_on: grantedOn,
        },
        { idempotencyKey: `extend:${caseId}:${clock.key}:${attempt}` },
      ),
    onSuccess: () => {
      setReason('')
      setOpen(false)
      refresh()
    },
  })

  if (clock.state === 'cannot-start') {
    return (
      <div className="blocked">
        <div>
          <div className="why">
            There is no time to extend until the period starts.{' '}
            {clock.blocked_reason ?? clock.describes}
          </div>
          <button type="button" className="small" disabled data-testid={`extend-${clock.key}`}>
            Grant more time
          </button>
        </div>
      </div>
    )
  }

  if (!open) {
    return (
      <button
        type="button"
        className="small"
        data-testid={`extend-${clock.key}`}
        onClick={() => setOpen(true)}
      >
        Grant more time
      </button>
    )
  }

  return (
    <div className="blocked note">
      <div style={{ width: '100%' }}>
        <div className="small muted">
          {clock.extension_headroom > 0
            ? `${clock.extension_headroom} days may still be granted on this period.`
            : 'No further time may be granted on this period.'}
        </div>
        <div className="row form">
          <label>
            Days
            <input
              type="number"
              min={1}
              aria-label="Days granted"
              value={days}
              onChange={(event) => setDays(Number(event.target.value))}
            />
          </label>
          <label>
            Granted on
            <input
              type="date"
              aria-label="Granted on"
              value={grantedOn}
              onChange={(event) => setGrantedOn(event.target.value)}
            />
          </label>
        </div>
        <label>
          Reasons recorded in writing
          <textarea
            rows={2}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </label>
        <div className="row">
          <button
            type="button"
            disabled={grant.isPending || !reason.trim()}
            onClick={() => grant.mutate()}
          >
            {grant.isPending ? 'Granting…' : 'Grant'}
          </button>
          <button type="button" onClick={() => setOpen(false)}>
            Cancel
          </button>
          {!reason.trim() ? (
            <span className="small muted">
              The reason is required — this is the authority&apos;s act to defend.
            </span>
          ) : null}
        </div>
        <ErrorEnvelope error={grant.error} />
      </div>
    </div>
  )
}

/* ------------------------------------------------------ skip / withdraw --- */

/**
 * Ruling a document off this case, or taking one back.
 *
 * The reason is mandatory and the database says so too. **A withdrawn
 * document's serial is spent and never reissued** — `_highest_serial` reads the
 * maximum across every state, so the number dies with the document.
 *
 * It does **not** retire the case's file number, and until P08's compliance
 * pass this component's own caption said it did — while the server, reading
 * that caption's promise literally, marked the live case's serial dead on every
 * withdrawal from any of the twenty rows. The case keeps its identity;
 * withdrawing the case is a different act, and there is no command for it yet.
 */
export function SkipOrWithdraw({ caseId, row }: { caseId: string; row: DocumentRow }) {
  const refresh = useRefresh(caseId)
  const [open, setOpen] = useState<'skip' | 'withdraw' | null>(null)
  const [reason, setReason] = useState('')

  const act = useMutation({
    mutationFn: () => {
      // The occurrence comes off the row rather than being assumed to be 1.
      // D03 and D06 are repeatable and D10 and D11 recurring, so "the D03 on
      // this case" is not a thing: withdrawing अनुस्मारक – II used to close
      // अनुस्मारक – I, which had gone out correctly.
      const body = {
        preflight: false,
        document_type: row.document_type,
        occurrence: Math.max(row.occurrences ?? 1, 1),
        reason: reason.trim(),
      }
      return open === 'withdraw'
        ? api.withdrawDocument(caseId, body)
        : api.skipDocument(caseId, body)
    },
    onSuccess: () => {
      setReason('')
      setOpen(null)
      refresh()
    },
  })

  if (open === null) {
    return (
      <span className="row" style={{ gap: 6 }}>
        <button type="button" className="small" onClick={() => setOpen('skip')}>
          Skip
        </button>
        <button type="button" className="small" onClick={() => setOpen('withdraw')}>
          Withdraw
        </button>
      </span>
    )
  }

  return (
    <div className="blocked unsettled">
      <div style={{ width: '100%' }}>
        <div className="why">
          {open === 'withdraw'
            ? "This document's serial is spent and never reissued. The case keeps its file number — withdrawing a document does not withdraw the case."
            : 'A skipped document stays on the rail with its reason. Nothing is filtered out.'}
        </div>
        <label>
          Reason
          <textarea
            rows={2}
            aria-label={`${open} reason`}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </label>
        <div className="row">
          <button
            type="button"
            disabled={act.isPending || !reason.trim()}
            onClick={() => act.mutate()}
          >
            {open === 'withdraw' ? 'Withdraw' : 'Skip'} {row.document_type}
          </button>
          <button type="button" onClick={() => setOpen(null)}>
            Cancel
          </button>
        </div>
        <ErrorEnvelope error={act.error} />
      </div>
    </div>
  )
}

/* ------------------------------------------------------------ timeline --- */

/**
 * What is on the record — the acts, not the documents.
 *
 * The rail shows what the case has produced. It cannot show what has been
 * *recorded about* it, and those are the things that move a case: service, a
 * decision, a reply, an appointment. Archit, 18 Sep 2026, after recording two
 * services and seeing nothing come back: *"wouldn't it be better if it can
 * show the records that were recorded."* A command that leaves no visible
 * trace is one an officer records twice — four identical served events on D01
 * are already on this database from exactly that.
 *
 * Case events, not audit rows: what happened in the *proceeding*. The audit
 * trail answers a different question and has its own screen, linked rather
 * than mixed in — a tribunal asks the two separately. The label is composed on
 * the server against the pinned version (stop list 6); the raw kind stays for
 * the audit screen, which wants the machine's name.
 */
export function Timeline({ data }: { data: CaseDetail }) {
  const events = data.events ?? []
  if (events.length === 0) return null
  return (
    <section className="papers-list" data-testid="timeline">
      <h3>
        On the record
        <span className="cnt mono">
          {events.length} {events.length === 1 ? 'act' : 'acts'}
        </span>
      </h3>
      <div>
        <div className="paper-line act-line paper-head small muted" aria-hidden="true">
          <span>Date</span>
          <span>What was recorded</span>
          <span>By</span>
        </div>
        {events.map((event, index) => {
          const row = event as Record<string, unknown>
          const payload = (row['payload'] ?? {}) as Record<string, unknown>
          return (
            <div
              className="paper paper-line act-line small"
              key={index}
              data-testid={`event-${String(row['seq'])}`}
            >
              <span className="mono muted">{readDate(String(row['occurred_on']))}</span>
              <span>
                {String(row['label'] ?? row['kind'])}
                {payload['label'] ? <> · {String(payload['label'])}</> : null}
                {payload['reason'] ? (
                  <span className="muted"> — {String(payload['reason'])}</span>
                ) : null}
              </span>
              <span className="muted">{String(row['actor'] ?? 'unknown')}</span>
            </div>
          )
        })}
      </div>
    </section>
  )
}
